import apiClient from "./api";

export interface AiExecutionLog {
  id: string;
  companyId: string;
  userId?: string;
  chatSessionId?: string;
  llmProvider: string;
  llmModel: string;
  httpStatusCode?: number;
  isSuccess: boolean;
  responseId?: string;
  executedAt: string;
  durationMs?: number;
  requestLength: number;
  responseLength: number;
  errorMessage?: string;
  agentRoute?: string;
}

// Sanitized request - only user-visible data
export interface UserMessage {
  role: string;
  content: string;
}

export interface KbEntry {
  sourceId: string;
  fileName: string;
  score: number;
  textPreview: string;
}

export interface ApiExecutionResult {
  toolName: string;
  callId: string;
  isSuccess: boolean;
}

/** A file the end user attached that was sent to the model on this call. */
export interface LoggedAttachmentInfo {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface SanitizedAiLogRequest {
  userMessages: UserMessage[];
  /** Empty for logs written before attachment metadata was persisted. */
  attachments: LoggedAttachmentInfo[];
  knowledgeBaseEntries: KbEntry[];
  injectedContext: string[];
  apiExecutionResults: ApiExecutionResult[];
  availableTools: string[];
  injectedSkills: string[];
  injectedCapabilities: string[];
  previousResponseId?: string;
  structuredOutputRequested: boolean;
}

// Sanitized response - only user-visible data
export interface ToolRequest {
  toolName: string;
  callId: string;
}

export interface SanitizedAiLogResponse {
  assistantMessage?: string;
  toolRequests: ToolRequest[];
  finishReason?: string;
  responseId?: string;
  hasChartData: boolean;
  hasTableData: boolean;
}

// Sanitized log detail - hides system instructions
export interface AiExecutionLogDetail {
  id: string;
  companyId: string;
  userId?: string;
  chatSessionId?: string;
  llmProvider: string;
  llmModel: string;
  request: SanitizedAiLogRequest;
  response: SanitizedAiLogResponse;
  httpStatusCode?: number;
  isSuccess: boolean;
  errorMessage?: string;
  responseId?: string;
  executedAt: string;
  durationMs?: number;
  agentRoute?: string;
  /**
   * True when the stored payload was cut at write time by the company's trace capture mode.
   * `request` and `response` are then parsed from a prefix and are usually empty — blank
   * sections mean "not retained", not "nothing was sent".
   */
  payloadTruncated?: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const logsService = {
  getAllLogs: async (
    page: number = 1,
    pageSize: number = 20,
    agentRoute?: string,
  ): Promise<PaginatedResult<AiExecutionLog>> => {
    const response = await apiClient.get(`/logs`, {
      params: { page, pageSize, ...(agentRoute ? { agentRoute } : {}) },
    });
    return response.data;
  },

  getLog: async (id: string): Promise<AiExecutionLogDetail> => {
    const response = await apiClient.get(`/logs/${id}`);
    return response.data;
  },

  getLogsBySession: async (
    chatSessionId: string,
  ): Promise<AiExecutionLog[]> => {
    const response = await apiClient.get(`/logs/session/${chatSessionId}`);
    return response.data;
  },
};
